import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Nav, Tab, Form, Button } from 'react-bootstrap';
import { getSolrData, useSolr } from '../../../hooks/useSolr';
import MandalaSkeleton from '../../common/MandalaSkeleton';
import './TermsGlossary.scss';
import { useKmap } from '../../../hooks/useKmap';
import { useInfiniteQuery } from 'react-query';
import { Link, useHistory } from 'react-router-dom';
import {
    BsBook,
    BsQuestion,
    BsSearch,
    FaExternalLinkAlt,
} from 'react-icons/all';
import TermDictionaries from '../TermDictionaries';
import { usePerspective } from '../../../hooks/usePerspective';
import TermsGlossaryNav from './TermsGlossaryNav';
import * as conv from '../../TranslationTool/src/DevanagariConverter';
import ReactHtmlParser from 'react-html-parser';
import { useDictionaryStore } from '../../../hooks/useDictionaryStore';
import { escapeStrayAngleBrackets } from '../../common/utils';
import TermsGlossarySearch from './TermsGlossarySearch';
import TermsGlossarySection from './TermsGlossarySection';

export function TermsGlossaryEntry({ data }) {
    // Dictionary Zustand Store
    const { dictionaries, showDictionary } = useDictionaryStore();
    // Component state to keep running track of number of definitions in total and of each type for display reasons
    const [activeDefs, setActiveDefs] = useState({
        total: 0,
        classical: 0,
        modern: 0,
    });

    let [deva, latin, defs] = ['', '', []];

    data._childDocuments_.forEach((child, key) => {
        if (child.block_child_type === 'related_names') {
            if (child.related_names_writing_system_code_s === 'deva') {
                deva = child.related_names_header_s;
            } else if (child.related_names_writing_system_code_s === 'latin') {
                latin = child.related_names_header_s;
            }
        }
    });

    const definitions = data._childDocuments_.filter((cd) => {
        return cd.block_child_type === 'related_definitions';
    });
    const defsbysource = definitions.reduce((dbs, def) => {
        if (!dbs[def.related_definitions_source_code_s]) {
            // If it doesn't exist, create an empty array for this key
            dbs[def.related_definitions_source_code_s] = [];
        }
        // Push the current object to the corresponding array
        dbs[def.related_definitions_source_code_s].push(def);
        return dbs;
    }, {});
    // related_terms_relation_code_s
    const variants = data._childDocuments_
        .filter((cd) => {
            return ['variant', 'is.variant', 'has.variant'].includes(
                cd.related_terms_relation_code_s
            );
        })
        .filter(
            (cd, cdindex, self) =>
                cdindex === self.findIndex((doc) => doc.id === cd.id)
        ); // filters out duplicate objects
    /*
    if (data.id === 'terms-371051') {
        console.log('variants ', variants);
    }*/
    const relterms = data._childDocuments_.filter((cd, cdindex, self) => {
        return (
            cd?.block_child_type === 'related_terms' &&
            ['has.as.a.part.of.the.phrase', 'is.part.of.the.phrase'].includes(
                cd?.related_terms_relation_code_s
            )
        );
    });

    /*
    const termvar = data.associated_subject_10453_ls.map((tkid, ind) => {
        return (
            <span key={`termvar-${ind}`}>
                {data.associated_subject_10453_ss[ind]} (
                {
                    data[
                        `related_terms_branch_10453_subjects_${tkid}_source_code_ss`
                    ]
                }
                )
                <MandalaPopover domain="subjects" kid={tkid} />
            </span>
        );
    }); */

    const classicalCodes =
        data?.related_terms_branch_10453_subjects_10455_source_code_ss || [];
    const modernCodes =
        data?.related_terms_branch_10453_subjects_10454_source_code_ss || [];
    let classicalDefs = Object.fromEntries(
        Object.entries(defsbysource).filter(([code, _]) =>
            classicalCodes.includes(code)
        )
    );
    classicalDefs = combineDefsWithOther(
        classicalDefs,
        classicalCodes,
        variants,
        relterms,
        data.id
    );

    let modernDefs = Object.fromEntries(
        Object.entries(defsbysource).filter(([code, _]) =>
            modernCodes.includes(code)
        )
    );

    modernDefs = combineDefsWithOther(
        modernDefs,
        modernCodes,
        variants,
        relterms,
        data.id
    );
    /*
    if (data.id === 'terms-248724') {
        console.log('data', data);
        console.log('variants', variants);
        console.log('defsbysource', defsbysource);
        console.log('filtered', classicalDefs, modernDefs);
        console.log('relterms', relterms, variants);
        console.log('classical Defs', classicalDefs);
    }
     */

    useEffect(() => {
        const cdefs = Object.keys(classicalDefs).filter((d) =>
            showDictionary(d)
        );
        const mdefs = Object.keys(modernDefs).filter((d) => showDictionary(d));
        let tempdefs = {
            classical: cdefs.length,
            modern: mdefs.length,
            total: cdefs.length + mdefs.length,
        };
        setActiveDefs(tempdefs);
    }, [dictionaries]);

    // If there are no defs, don't show it.
    if (activeDefs.total === 0) {
        return null;
    }

    return (
        <div
            key={`entrydiv-${data.id}`}
            className="glossary entry"
            data-kid={data.id}
        >
            <h3>
                <span className="ne">{deva}</span>{' '}
                <span className="en font-times">
                    (<LatinFix latin={latin} />)
                </span>
                <Link
                    to={`/${data.id.replace('-', '/')}`}
                    className="gloss-entry-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View in dictionary."
                >
                    <FaExternalLinkAlt />
                </Link>
            </h3>
            {/*
            <TermGlossaryVariants
                variants={variants}
                data={data}
                relterms={relterms}
            />
            */}
            {/*<TermGlossaryRelatedSubjects data={data} />*/}
            {/*
            {Object.keys(defsbysource).length === 0 && (
                <div className="sui-termDicts__entry-variant">
                    <span className="font-weight-bold">Variant: </span>
                    {termvar}
                </div>
            )}
            */}
            {activeDefs.classical > 0 && (
                <div>
                    <h4>Classical Newar</h4>
                    <TermDictionaries
                        definitions={classicalDefs}
                        kmapData={data}
                    />
                </div>
            )}
            {activeDefs.modern > 0 && (
                <div>
                    <h4>Modern Newar</h4>
                    <TermDictionaries
                        definitions={modernDefs}
                        kmapData={data}
                    />
                </div>
            )}
            {/*
            {variants?.length > 0 && (
                <div className="sui-termDicts__dict-variants">
                    <h5>Variants: </h5>
                    <ul className="sui-termDicts__dict-wrapper">
                        {variants.map((vdata, vi) => (
                            <li
                                className="sui-termDicts__dict"
                                key={`${data.id}-var-${vi}`}
                            >
                                <GlossaryVariant
                                    kid={vdata.related_terms_id_s}
                                    code={vdata.related_terms_relation_code_s}
                                    label={vdata.related_terms_relation_label_s}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {relterms?.length > 0 && (
                <div className="sui-termDicts__dict-variants">
                    <h5>Related Terms:</h5>
                    <ul className="sui-termDicts__dict-wrapper">
                        {relterms.map((rt, rti) => {
                            return (
                                <li
                                    className="sui-termDicts__dict-def"
                                    key={[data.id, 'relterm', rti].join('-')}
                                >
                                    {rt.related_terms_relation_label_s}{' '}
                                    <GlossaryVariant
                                        kid={rt.related_terms_id_s}
                                        code={rt.related_terms_relation_code_s}
                                        label={
                                            rt.related_terms_relation_label_s
                                        }
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )} */}
        </div>
    );
}

/**
 * Takes an object of keyed def lists and prepends variants and related terms from the same dictionary to the list
 * for that dictionary. Returning the object with aggregated lists.
 *
 * @param defs
 * @param variants
 * @param relterms
 * @param dataid
 * @returns {*}
 */
function combineDefsWithOther(defs, validcodes, variants, relterms, dataid) {
    /*if (dataid === 'terms-248724') {
        console.log(defs, variants, relterms, dataid);
        console.log('before', defs);
    }*/

    const combined = {};
    for (const [dictkey, deflist] of Object.entries(defs)) {
        if (!Array.isArray(deflist)) {
            console.log('NOt an array', deflist);
        }
        if (!(dictkey in combined)) {
            combined[dictkey] = deflist;
        } else {
            combined[dictkey].push(...deflist);
        }
    }

    variants = variants.filter((v) => {
        if (
            'related_terms_relation_source_code_ss' in v &&
            v['related_terms_relation_source_code_ss'].length > 0
        ) {
            return validcodes.includes(
                v['related_terms_relation_source_code_ss'][0]
            );
        } else {
            return false;
        }
    });

    //

    const groupedvariants = Object.values(
        variants.reduce((acc, item) => {
            let { source, related_terms_relation_code_s, related_uid_s } = item;
            if (!source) {
                source = '';
            }
            const acckey = source + related_terms_relation_code_s;
            if (!acc[acckey]) {
                acc[acckey] = {
                    ...item,
                    source,
                    related_uid_s: [related_uid_s],
                };
            } else {
                acc[acckey].related_uid_s.push(related_uid_s);
            }
            return acc;
        }, {})
    );

    /*
    if (dataid === 'terms-356449') {
        console.log('grouped vars', groupedvariants);
    }*/
    groupedvariants.forEach((variant) => {
        if (variant['related_terms_relation_source_code_ss']?.length > 0) {
            const dictkey = variant['related_terms_relation_source_code_ss'][0];
            if (!(dictkey in combined)) {
                combined[dictkey] = [variant];
            } else {
                combined[dictkey].push(variant);
            }
        }
    });

    const groupedrelterms = Object.values(
        relterms.reduce((acc, item) => {
            let { source, related_terms_relation_code_s, related_uid_s } = item;
            if (!source) {
                source = '';
            }
            const acckey = source + related_terms_relation_code_s;
            if (!acc[acckey]) {
                acc[acckey] = {
                    ...item,
                    source,
                    related_uid_s: [related_uid_s],
                };
            } else {
                acc[acckey].related_uid_s.push(related_uid_s);
            }
            return acc;
        }, {})
    );

    groupedrelterms.forEach((relterm) => {
        if (relterm['related_terms_relation_source_code_ss']?.length > 0) {
            const dictkey = relterm['related_terms_relation_source_code_ss'][0];
            if (validcodes.includes(dictkey)) {
                if (!(dictkey in combined)) {
                    combined[dictkey] = [relterm];
                } else {
                    combined[dictkey].push(relterm);
                }
            }
        }
    });
    /*
    if (dataid === 'terms-248724') {
        console.log("combined", combined);
    }
     */
    return combined;
}

function TermGlossaryVariants({ variants, data, relterms }) {
    return (
        <div className="sui-termDicts__dict-variants">
            <ul className="sui-termDicts__dict-wrapper">
                {variants.map((vdata, vi) => {
                    let repeat = false; // Doesn't work.
                    /*
                    if (vi > 0) {
                        repeat = vdata.related_terms_relation_code_s === variants[vi - 1].related_terms_relation_code_s;
                    }*/
                    return (
                        <li
                            className="sui-termDicts__dict"
                            key={`${data.id}-var-${vi}`}
                        >
                            {vdata.related_terms_relation_label_s}
                            <GlossaryTermDisplay
                                kid={vdata.related_terms_id_s}
                            />
                        </li>
                    );
                })}
                {relterms.map((rt, rti) => {
                    return (
                        <li
                            className="sui-termDicts__dict-def"
                            key={[data.id, 'relterm', rti].join('-')}
                        >
                            {rt.related_terms_relation_label_s}
                            <GlossaryTermDisplay kid={rt.related_terms_id_s} />(
                            {rt.related_terms_relation_source_code_ss})
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export function GlossaryTermDisplay({ kid }) {
    const { setStartAt, setSearchTerm } = useDictionaryStore();

    const {
        isLoading: isAssetLoading,
        data: kmasset,
        isError: isAssetError,
        error: assetError,
    } = useKmap(kid, 'info');

    // console.log("kid", kid);
    if (isAssetLoading) {
        return null;
    }

    if (isAssetError) {
        console.log('asset error', assetError);
        return null;
    }

    let linkpath;
    let nativeterm;

    if ('level_new.alpha_i' in kmasset && kmasset['level_new.alpha_i'] > 0) {
        linkpath = '/terms/glossary/nod/';
        nativeterm = (
            <span className="ne d-inline-block ml-2" data-mid={kid}>
                {kmasset?.name_deva[0]}
            </span>
        );
    } else if (kmasset?.name_tibt) {
        linkpath = '/terms/';
        nativeterm = (
            <span className="bo d-inline-block ml-2" data-mid={kid}>
                {kmasset?.name_tibt[0]}
            </span>
        );
    }

    // Adds definitions of term to title in link for popover
    const defs =
        kmasset?._childDocuments_?.length > 0
            ? kmasset._childDocuments_
                  .map((cd, cdi) => {
                      if (cd?.related_definitions_content_latinu) {
                          let latinu = cd.related_definitions_content_latinu;
                          return escapeStrayAngleBrackets(latinu);
                      }
                      return null;
                  })
                  .filter((cd) => {
                      return cd;
                  })
            : [];

    let tstr = defs.join('. ').replace(/\.\./g, '.').trim();
    tstr += ' (Click to view term in dictionary.)';

    // console.log('variant', kmasset, defs);
    /// console.log('title str', tstr);
    if ('level_new.alpha_i' in kmasset && kmasset['level_new.alpha_i'] > 0) {
        const uid = +kid.replace('terms-', '');
        return (
            <>
                <Link
                    to={linkpath}
                    title={tstr.trim()}
                    onClick={() => {
                        setSearchTerm('');
                        setStartAt(uid);
                    }}
                >
                    {nativeterm}{' '}
                    <span className="font-times">
                        (<LatinFix latin={kmasset?.name_latin[0]} />)
                    </span>
                </Link>
            </>
        );
    } else {
        return (
            <>
                <Link
                    to={linkpath + kid.replace('terms-', '')}
                    title={tstr.trim()}
                >
                    {nativeterm}{' '}
                    <span className="font-times">
                        (<LatinFix latin={kmasset?.name_latin[0]} />)
                    </span>
                </Link>
            </>
        );
    }
}

function TermGlossaryRelatedSubjects({ data }) {
    const nonexpsubids = [];
    const nonexpsubjs = data?.associated_subjects.filter((das, dasi) => {
        if (das !== 'Expression') {
            nonexpsubids.push(data.associated_subject_ids[dasi]);
            return true;
        }
        return false;
    });
    if (nonexpsubjs?.length > 0) {
        return (
            <div className="ml-3">
                <strong>Variants: </strong>{' '}
                {nonexpsubjs?.map((d, di) => {
                    const slink = `/subjects/${nonexpsubids[di]}`;
                    return (
                        <a
                            key={[d, di].join('-')}
                            href={slink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {d}
                        </a>
                    );
                })}
            </div>
        );
    }
    return null;
}

export function LatinFix({ latin }) {
    const badcb = 'm\u0306\u0307'; // chandra + bindu (wrong)
    const goodcb = 'm\u0310'; // '<span class="chandrabindu">m\u0310</span>'; // chandrabindu (right)
    if (latin.includes(badcb)) {
        latin = latin.replace(badcb, goodcb);
    }
    return ReactHtmlParser(latin);
}
